import React, { useState } from 'react';
import { applyAsTrainer, getMyTrainerApplication } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import { GraduationCap, Loader2, CheckCircle, Clock, XCircle } from 'lucide-react';

export default function TrainerApply(): React.JSX.Element {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    expertise: '',
    bio: '',
    phone: '',
    sample_video_url: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await applyAsTrainer(form);
      toast.show('Application submitted successfully!', 'success');
      setSubmitted(true);
    } catch (err: any) {
      toast.show(err.message || 'Failed to submit application', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Application Submitted!</h1>
          <p className="text-slate-600 mb-6">
            Our team will review your application and get back to you within 3-5 business days.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <Clock className="w-4 h-4" />
            <span>Status: Pending Review</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
          <GraduationCap className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Become an Instructor</h1>
        <p className="text-slate-600">
          Share your expertise with thousands of learners worldwide.
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Area of Expertise</label>
            <Input
              placeholder="e.g., Machine Learning, Web Development"
              value={form.expertise}
              onChange={(e) => setForm({ ...form, expertise: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
            <textarea
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all min-h-[120px] resize-y"
              placeholder="Tell us about your experience and teaching style..."
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone (optional)</label>
            <Input
              placeholder="+91 98765 43210"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sample Video URL (optional)</label>
            <Input
              placeholder="https://youtube.com/watch?v=..."
              value={form.sample_video_url}
              onChange={(e) => setForm({ ...form, sample_video_url: e.target.value })}
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Application'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
